import { useCallback, useEffect, useState } from 'react'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'
import ProjectSheet from '../components/ProjectSheet.jsx'
import NoteSheet from '../components/NoteSheet.jsx'
import Sheet from '../components/Sheet.jsx'
import Fasting from '../components/Fasting.jsx'
import Weight from '../components/Weight.jsx'
import {
  addProject, getBoard, getEntries, getTopics, isHealth, updateEntry,
} from '../lib/notes.js'

/**
 * One category. Projects are the adaptive list; the selected project's plain
 * column of dash items is its detail. No kanban, nothing drags. Category-wide
 * health tools and notes follow the list rather than belonging to a project.
 *
 * Done is struck through in place, then folds into a collapsed count. Nothing
 * is deleted, and the Log keeps the record either way.
 */
export default function Category({ category, toast, refreshKey, jump = null, onJumpHandled }) {
  const [board, setBoard] = useState(null)
  const [notes, setNotes] = useState([])
  const [topics, setTopics] = useState([])
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  /* One selection across both lists, so projects and notes share the split
     view: 'p:<id>' for a project, 'n:<id>' for a note. */
  const [selectedKey, setSelectedKey] = useState(null)
  const [loadedFor, setLoadedFor] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [b, n, t] = await Promise.all([
        getBoard(category.id), getEntries(category.id), getTopics(category.id),
      ])
      setBoard(b)
      setNotes(n)
      setTopics(t)
      setLoadedFor(category.id)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [category.id])

  useEffect(() => { load() }, [load, refreshKey])
  useEffect(() => { setSelectedKey(null) }, [category.id])

  /* Arriving from a link or the Log: once this category's own lists are in,
     select the note or project asked for. Lists still showing the previous
     category are not an answer, so wait for this one's load. */
  useEffect(() => {
    if (!jump || (jump.type !== 'topic' && jump.type !== 'project')) return
    if (loadedFor !== category.id) return
    const key = (jump.type === 'topic' ? 'n:' : 'p:') + jump.id
    const found = jump.type === 'topic'
      ? topics.some((t) => t.id === jump.id)
      : board?.projects.some((p) => p.id === jump.id)
    if (found) setSelectedKey(key)
    onJumpHandled?.()
  }, [jump, loadedFor, category.id, topics, board, onJumpHandled])

  const selectedProject = board?.projects.find((project) => 'p:' + project.id === selectedKey) ?? null
  const selectedNote = topics.find((topic) => 'n:' + topic.id === selectedKey) ?? null
  const draftingNote = selectedKey === 'n:new'
  const newProjectAction = board ? (
    <button type="button" className="btn ghost split-new-action"
      onClick={() => setSheet({ kind: 'project' })}>
      New project
    </button>
  ) : null

  const categoryFooter = (
    <>
      {/* After the projects and their New project button on a phone, and
          before quick captures, so each list keeps its own add button. */}
      {board ? (
        <>
          <p className="lbl" style={{ margin: '24px 2px 8px' }}>Notes</p>
          {topics.length ? (
            <div className="card">
              <div className="rows">
                {topics.map((topic) => (
                  <button
                    type="button"
                    className={'row project-row' + (selectedNote?.id === topic.id ? ' selected' : '')}
                    key={topic.id}
                    data-detail-id={'n:' + topic.id}
                    aria-current={selectedNote?.id === topic.id ? 'true' : undefined}
                    onClick={() => setSelectedKey('n:' + topic.id)}
                  >
                    <span className="nm">
                      <b style={{ whiteSpace: 'normal' }}>{topic.title}</b>
                      <span className="lbl">
                        {topic.entry_count} {topic.entry_count === 1 ? 'entry' : 'entries'}
                        {' · added '}
                        {new Date(topic.last_added).toLocaleDateString('en-PH', {
                          day: 'numeric', month: 'short',
                        })}
                      </span>
                    </span>
                    <span className="go" aria-hidden="true">›</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <button type="button" className="btn ghost split-new-action"
            onClick={() => setSelectedKey('n:new')}>
            New note
          </button>
        </>
      ) : null}
      {isHealth(category) ? <Weight toast={toast} /> : null}
      {isHealth(category) ? <Fasting toast={toast} /> : null}

      {notes.length ? (
        <>
          <p className="lbl" style={{ margin: '24px 2px 8px' }}>Quick captures</p>
          <div className="card">
            <div className="rows">
              {notes.map((note) => (
                <button
                  type="button"
                  className="row"
                  key={note.id}
                  aria-label={'Edit capture: ' + note.body}
                  onClick={() => setSheet({ kind: 'note', note })}
                >
                  <span className="nm">
                    <b style={{ whiteSpace: 'normal' }}>{note.body}</b>
                    <span className="lbl">
                      {new Date(note.created_at).toLocaleDateString('en-PH', {
                        day: 'numeric', month: 'short',
                      })}
                    </span>
                  </span>
                  <span className="go" aria-hidden="true">›</span>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.categoryWidth"
        listLabel={category.name + ' projects'}
        selectedId={selectedProject || selectedNote || draftingNote ? selectedKey : null}
        emptyLabel="Select a project or note"
        toolbarAction={newProjectAction}
        listFooter={categoryFooter}
        list={(
          <>
            <div className="page-head">
              <span className="lbl">
                <i className="swatch" style={{ background: category.color || 'var(--ink-faint)' }} />
                {' '}Category
              </span>
              <h1 className="serif">{category.name}</h1>
            </div>

            {error ? <div className="notice warn"><p style={{ margin: 0, fontSize: 14 }}>{error}</p></div> : null}
            {!board && !error ? <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p> : null}

            {board && board.projects.length === 0 && topics.length === 0 ? (
              <div className="empty">
                <h2 className="serif">No projects yet</h2>
                <p>Add a project for anything with more than one step.</p>
              </div>
            ) : null}

            {board?.projects.length ? (
              <div className="card">
                <div className="rows">
                  {board.projects.map((project) => {
                    const items = board.items.filter((item) => item.project_id === project.id)
                    const openCount = items.filter((item) => item.status !== 'Done').length
                    const doneCount = items.length - openCount
                    return (
                      <button
                        type="button"
                        className={'row project-row' + (selectedProject?.id === project.id ? ' selected' : '')}
                        key={project.id}
                        data-detail-id={'p:' + project.id}
                        aria-current={selectedProject?.id === project.id ? 'true' : undefined}
                        onClick={() => setSelectedKey('p:' + project.id)}
                      >
                        <span className="nm">
                          <b>{project.title}</b>
                          <span className="lbl">
                            {openCount} open{doneCount ? ' · ' + doneCount + ' done' : ''}
                          </span>
                        </span>
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}

          </>
        )}
        detail={selectedProject ? (
          <ProjectSheet
            key={selectedProject.id}
            project={selectedProject}
            toast={toast}
            onClose={() => setSelectedKey(null)}
            onChanged={load}
          />
        ) : draftingNote ? (
          <NoteSheet
            key="new"
            note={null}
            categoryId={category.id}
            toast={toast}
            onClose={() => setSelectedKey(null)}
            onCreated={async (topic) => {
              await load()
              setSelectedKey('n:' + topic.id)
            }}
          />
        ) : selectedNote ? (
          <NoteSheet
            key={selectedNote.id}
            note={selectedNote}
            toast={toast}
            onClose={() => setSelectedKey(null)}
            onChanged={load}
          />
        ) : null}
      />

      {sheet?.kind === 'project' ? (
        <TextSheet
          title="New project"
          label="Project title"
          onClose={() => setSheet(null)}
          onSave={async (v) => {
            await addProject({ title: v, categoryId: category.id })
            setSheet(null)
            load()
          }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'note' ? (
        <TextSheet
          title="Edit capture"
          label="Capture"
          initial={sheet.note.body}
          multiline
          onClose={() => setSheet(null)}
          onSave={async (v) => {
            if (v !== sheet.note.body) await updateEntry(sheet.note.id, v)
            setSheet(null)
            load()
          }}
          toast={toast}
        />
      ) : null}

    </>
  )
}

function TextSheet({ title, label, initial = '', multiline = false, onClose, onSave, toast }) {
  const [v, setV] = useState(initial)
  const [busy, setBusy] = useState(false)
  return (
    <Sheet title={title} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !v.trim()) return
          setBusy(true)
          try {
            await onSave(v.trim())
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="ts-v">{label}</label>
          {multiline ? (
            <textarea id="ts-v" rows={4} value={v} onChange={(e) => setV(e.target.value)} required />
          ) : (
            <input id="ts-v" value={v} onChange={(e) => setV(e.target.value)} required />
          )}
        </div>
        <button className="btn" type="submit" disabled={busy || !v.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Sheet>
  )
}
